import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { ArrowRight, Eye, EyeOff, Wifi, TrendingUp, MapPin, Shield, Target, Building2, ChevronRight, Smartphone, Radio, CheckCircle2, Sparkles, Download, Linkedin, Link2, Check } from 'lucide-react';
import useCardShare from '../hooks/useCardShare';
import BoroughAmplificationMap from '../components/BoroughAmplificationMap';

const API = process.env.REACT_APP_BACKEND_URL;

function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * end));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

function AnimatedDecimal({ end, duration = 2000, decimals = 1, suffix = '' }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(eased * end);
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{value.toFixed(decimals)}{suffix}</span>;
}

function ConnectivityRing({ percent, size = 88, label, sublabel, accent = '#FE4A2A', delay = 0 }) {
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        setTimeout(() => {
          const start = performance.now();
          const step = (now) => {
            const p = Math.min((now - start) / 1800, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setProgress(eased * percent);
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }, delay);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [percent, delay]);

  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div ref={ref} className="flex flex-col items-center text-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#090909" strokeOpacity="0.07" strokeWidth="5" />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={accent} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 60ms linear' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-display text-base sm:text-lg font-extrabold text-white leading-none">
            {progress >= 99.95 ? '100%' : progress.toFixed(progress < 10 ? 1 : progress >= 90 ? 1 : 2).replace(/\.?0+$/, '') + '%'}
          </span>
        </div>
      </div>
      <div className="font-body text-[10px] uppercase tracking-wider text-white/55 mt-2 font-medium">{label}</div>
      {sublabel && <div className="font-body text-[9px] text-white/35 mt-0.5">{sublabel}</div>}
    </div>
  );
}

const TIER_COLORS = {
  trading_invisible: '#8A1A1A', shell: '#5A5751', dissolved: '#B8B4AD',
  fully_visible: '#2D6E4E', partially_visible: '#6B9E56',
};

function ShareBoroughReality() {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.origin + '/' : '';
  const shareText = 'Stockton-on-Tees has world-class connectivity infrastructure. 95% Gigabit-capable, 59.2% FTTP, 0% below USO. But 55% of businesses are digitally invisible. Coverage is not connectivity.';

  const { liUrl, downloadPng, openLinkedIn, copyLink } = useCardShare({
    target: 'borough',
    cardPath: 'borough-reality.png',
    shareUrl,
    caption: shareText,
    filename: 'stockton-borough-reality.png',
  });

  const handleCopy = async () => {
    const ok = await copyLink();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="share-borough-reality">
      <button
        onClick={downloadPng}
        title="Download Borough Reality Card (500×500 PNG)"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-[#FE4A2A]/40 transition-colors font-body text-[11px] text-white/75"
        data-testid="share-borough-reality-download"
      >
        <Download className="w-3 h-3" strokeWidth={2} />
        Borough Reality Card
      </button>
      <button
        onClick={openLinkedIn}
        title="Share on LinkedIn (also downloads PNG)"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/[0.04] hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50 transition-colors text-white/70 hover:text-white"
        data-testid="share-borough-reality-linkedin"
        data-linkedin-url={liUrl}
      >
        <Linkedin className="w-3 h-3" strokeWidth={2} />
      </button>
      <button
        onClick={handleCopy}
        title="Copy link + caption (also downloads PNG)"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-[#FE4A2A]/40 transition-colors text-white/70 hover:text-white"
        data-testid="share-borough-reality-copy"
      >
        {copied ? <Check className="w-3 h-3 text-[#2D6E4E]" strokeWidth={2.5} /> : <Link2 className="w-3 h-3" strokeWidth={2} />}
      </button>
    </div>
  );
}

export default function PublicDashboard() {
  const [stats, setStats] = useState(null);
  const [activeLA, setActiveLA] = useState('tvca');
  const navigate = useNavigate();

  useEffect(() => {
    const laParam = activeLA && activeLA !== 'tvca' ? `&la=${activeLA}` : '';
    fetch(`${API}/api/public/stats?_t=${Date.now()}${laParam}`).then(r => r.json()).then(setStats).catch(console.error);
  }, [activeLA]);

  const LA_OPTIONS = [
    { slug: 'tvca', label: 'All Tees Valley', short: 'TVCA' },
    { slug: 'stockton', label: 'Stockton-on-Tees', short: 'Stockton' },
    { slug: 'middlesbrough', label: 'Middlesbrough', short: 'Middlesbrough' },
    { slug: 'redcar', label: 'Redcar & Cleveland', short: 'Redcar' },
    { slug: 'hartlepool', label: 'Hartlepool', short: 'Hartlepool' },
    { slug: 'darlington', label: 'Darlington', short: 'Darlington' },
  ];

  if (!stats) return (
    <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
      <div className="w-8 h-8 rounded-[4px] bg-brand-orange animate-pulse" />
    </div>
  );

  const td = stats.tier_distribution || {};
  const tierData = [
    { name: 'Trading Invisible', value: td.trading_invisible || stats.data_quality?.trading_invisible || 4749, fill: TIER_COLORS.trading_invisible, group: 'invisible' },
    { name: 'Shell / Non-Trading', value: td.shell_non_trading || stats.data_quality?.shell_non_trading || 587, fill: TIER_COLORS.shell, group: 'invisible' },
    { name: 'Dissolved / Winding', value: td.dissolved_dormant || stats.data_quality?.dissolved_dormant || 1181, fill: TIER_COLORS.dissolved, group: 'invisible' },
    { name: 'Partially Visible', value: td.partially_visible || 2171, fill: TIER_COLORS.partially_visible, group: 'visible' },
    { name: 'Fully Visible', value: td.fully_visible || stats.digitally_active || 3161, fill: TIER_COLORS.fully_visible, group: 'visible' },
  ];
  const invisibleTotal = tierData.filter(t => t.group === 'invisible').reduce((a, t) => a + t.value, 0);
  const visibleTotal = tierData.filter(t => t.group === 'visible').reduce((a, t) => a + t.value, 0);

  const districtData = stats.districts.map(d => ({
    district: d.district,
    active: d.active,
    partial: d.partial,
    invisible: d.invisible,
  }));

  const invisiblePct = Math.round(stats.digitally_invisible / Math.max(stats.total_businesses, 1) * 100);

  // Scope-aware helpers — landing copy adapts to the active LA (or TVCA)
  const isTVCA = !activeLA || activeLA === 'tvca';
  const scopeName = isTVCA ? 'the Tees Valley' : (stats.scope_name || 'the Tees Valley');
  const glanceName = isTVCA ? 'Tees Valley' : (stats.scope_name || 'Tees Valley');
  const fixed = stats.connectivity?.fixed || {};
  const mobile = stats.connectivity?.mobile || {};
  const worstDistricts = [...(stats.districts || [])]
    .filter(d => (d.total || 0) >= 100)
    .map(d => ({ ...d, invPct: Math.round((d.invisible || 0) / Math.max(d.total, 1) * 100) }))
    .sort((a, b) => b.invPct - a.invPct)
    .slice(0, 2);

  // Real headline insights (BDAF Final × IoD 2025 × Ofcom Jan 2026), computed per scope
  const hi = stats.headline_insights || null;
  const hiReg = hi?.register;
  const hiTrad = hi?.trading;
  const hiSignals = hi?.signals;
  const hiSegments = hi?.segments;
  const corrImd = hi?.correlations?.imd_vs_silence;
  const corrSpeed = hi?.correlations?.speed_vs_silence;

  return (
    <div className="min-h-screen bg-[#FEFEFE] text-[#090909] overflow-hidden" data-testid="public-dashboard">
      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      {/* ═══ TOP NAV — Sign In CTA ═══ */}
      <nav className="sticky top-0 z-40 bg-[#FEFEFE]/90 backdrop-blur-md border-b border-[#090909]/8" data-testid="public-top-nav">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-5 sm:h-6 w-auto brightness-0" testId="nav-logo" />
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="public-nav-verified"
              onClick={() => navigate('/verified')}
              className="hidden sm:inline font-body text-xs text-[#090909]/60 hover:text-[#090909] transition-colors px-3 py-1.5"
            >
              Verified businesses
            </button>
            <button
              data-testid="public-nav-signin"
              onClick={() => navigate('/login')}
              className="font-body text-xs text-[#090909]/60 hover:text-[#090909] transition-colors px-3 py-1.5"
            >
              Sign In
            </button>
            <button
              data-testid="public-nav-register"
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-1 bg-[#090909] text-white px-4 py-1.5 rounded-[3px] font-body text-xs font-medium hover:bg-[#FE4A2A] transition-colors"
            >
              Request Access <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#FE4A2A] rounded-full opacity-[0.04] blur-[120px]" />
          <div className="absolute bottom-20 left-10 w-[400px] h-[400px] bg-[#FE4A2A] rounded-full opacity-[0.03] blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 py-20">
          <div className="flex items-center gap-3 mb-6">
            <BrandLogo className="h-7 sm:h-8 w-auto brightness-0" testId="hero-logo" />
            <span className="hidden sm:inline-block w-px h-5 bg-[#090909]/15" />
            <span className="font-body text-[11px] uppercase tracking-[0.18em] text-[#FE4A2A] font-bold">Tees Valley Business Digital Inclusion Intelligence</span>
          </div>

          {/* Local Authority chip selector */}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="la-selector">
            {LA_OPTIONS.map(opt => (
              <button
                key={opt.slug}
                data-testid={`la-chip-${opt.slug}`}
                onClick={() => setActiveLA(opt.slug)}
                className={`font-body text-[11px] px-3 py-1.5 rounded-full border transition-all ${
                  activeLA === opt.slug
                    ? 'bg-[#090909] text-white border-[#090909]'
                    : 'bg-white text-[#5A5751] border-[#090909]/15 hover:border-[#090909]/40 hover:text-[#090909]'
                }`}
              >
                {opt.short}
              </button>
            ))}
          </div>
          {activeLA && activeLA !== 'tvca' && (
            <div className="mb-6">
              <button
                onClick={() => navigate(`/authority/${activeLA}`)}
                data-testid={`view-authority-${activeLA}`}
                className="inline-flex items-center gap-1.5 font-body text-[11px] text-[#FE4A2A] hover:underline"
              >
                View full {LA_OPTIONS.find(l => l.slug === activeLA)?.label} report
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[4rem] font-bold tracking-tight leading-[1.02] max-w-4xl mb-6" data-testid="hero-title">
            The businesses missing<br />from your digital economy.<br /><span className="text-[#FE4A2A]">Made visible.</span>
          </h1>

          <p className="font-body text-base sm:text-lg text-[#090909]/80 max-w-3xl leading-relaxed mb-6 font-medium" data-testid="hero-sub-headline">
            Adoptic scores every registered business in your geography for digital presence — without surveys, without consultants, in minutes. Then it plans the intervention, measures the impact, and generates the evidence your funder requires.
          </p>

          <div className="inline-flex items-center gap-2 mb-10 px-3 py-1.5 rounded-full bg-[#FECFA5]/40 border border-[#FECFA5]" data-testid="hero-live-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FE4A2A] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FE4A2A]" />
            </span>
            <span className="font-body text-[11px] text-[#742618] font-semibold">{(stats.digitally_invisible || 0).toLocaleString()} of {(stats.total_businesses || 0).toLocaleString()} in {stats.scope_name || 'Tees Valley'} scoring 0–1</span>
            <span className="font-body text-[10px] text-[#742618]/60">· Updated today</span>
          </div>

          <div className="flex flex-wrap gap-3 mb-16">
            <button data-testid="hero-cta" onClick={() => navigate('/login')} className="flex items-center gap-2 bg-[#FE4A2A] text-white px-6 py-3 rounded-[2px] font-body text-sm font-medium hover:bg-[#F14728] transition-colors shadow-[0_0_15px_rgba(254,74,42,0.3)]">
              Explore the Register <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#problem" className="flex items-center gap-2 border border-[#090909]/15 px-6 py-3 rounded-[2px] font-body text-sm text-[#5A5751] hover:border-[#090909]/30 transition-colors">
              Learn More <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Hero stat strip — 4 evidence cards per Adoptic copy v1.0 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { value: hiReg?.pct_0_1 ?? 22.5, label: `of the ${glanceName} register scores 0–1 on the five BDAF signals — silent or near-silent online`, suffix: '%', decimals: 1 },
              { value: hiTrad?.invisible_0_pct ?? 6.8, label: 'of trading businesses score zero on all five signals — completely invisible, despite great broadband', suffix: '%', decimals: 1 },
              { value: 16.5, label: 'lost to the UK economy every year from business digital exclusion', prefix: '£', suffix: 'B', decimals: 1 },
              { value: 0, label: 'other platforms that measure this at postcode level, continuously, without surveys', suffix: '', decimals: 0 },
            ].map((s, i) => (
              <div key={s.label} className="backdrop-blur-2xl bg-white/60 border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-5 rounded-[4px]" data-testid={`hero-stat-${i}`}>
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#FE4A2A]">
                  {s.prefix || ''}{s.decimals > 0
                    ? <AnimatedDecimal end={s.value} suffix={s.suffix || ''} decimals={s.decimals} />
                    : <AnimatedCounter end={s.value} suffix={s.suffix || ''} />}
                </div>
                <div className="font-body text-[11px] text-[#5A5751] mt-2 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CONNECTIVITY CALLOUT — every premises is connected ═══ */}
      <section className="relative py-20 px-6 lg:px-8 overflow-hidden bg-[#090909] text-white" data-testid="connectivity-callout">
        {/* Subtle radial accent */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#FE4A2A]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#2D6E4E]/10 blur-3xl pointer-events-none" />

        <div className="max-w-[1200px] mx-auto relative">
          {/* Header line */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative">
              <div className="w-2 h-2 rounded-full bg-[#2D6E4E]" />
              <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#2D6E4E] animate-ping" />
            </div>
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#2D6E4E]">Live · Ofcom Connected Nations · Jan 2026</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Narrative side */}
            <div className="lg:col-span-5">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tighter leading-[0.95] mb-5">
                Every premises<br />
                <span className="text-[#FE4A2A]">is connected.</span>
              </h2>
              <p className="font-body text-base text-white/75 leading-relaxed mb-5 max-w-md">
                {glanceName} has effectively zero connectivity gap.
                <strong className="text-white"> {fixed.below_uso_pct ?? 0}%</strong> of premises sit below the Universal Service Obligation.
                <strong className="text-white"> {fixed.gigabit_pct ?? 95}%</strong> are Gigabit-capable, <strong className="text-white"> {fixed.full_fibre_pct ?? 59.2}%</strong> have full fibre (FTTP),
                and <strong className="text-white"> {mobile['4g_outdoor_pct'] ?? 99.9}%</strong> have 4G outdoors.
              </p>
              <p className="font-body text-[11px] text-white/40 leading-relaxed mb-5 max-w-md italic">
                Gigabit-capable means any technology delivering ≥1 Gbit/s — including DOCSIS 3.1 cable and full fibre. FTTP refers specifically to full-fibre to the premises.
              </p>
              <p className="font-body text-sm text-white/55 leading-relaxed mb-6 max-w-md">
                The infrastructure is here. The connection isn&apos;t the problem.
                <strong className="text-white"> Adoption is.</strong> That&apos;s the case for intervention — and the entire reason this register exists.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 font-body text-[11px] text-white/70">
                  <CheckCircle2 className="w-3 h-3 text-[#2D6E4E]" /> No coverage gaps
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 font-body text-[11px] text-white/70">
                  <Sparkles className="w-3 h-3 text-[#FE4A2A]" /> No excuses for invisibility
                </span>
              </div>
            </div>

            {/* Stats side */}
            <div className="lg:col-span-7">
              {/* 5 ring metrics — differentiated infrastructure types */}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 sm:gap-3 lg:gap-4 mb-6">
                <ConnectivityRing percent={fixed.gigabit_pct ?? 95}       label="Gigabit" sublabel="Any tech ≥1 Gbit/s" accent="#FE4A2A" delay={0} />
                <ConnectivityRing percent={fixed.full_fibre_pct ?? 59.2}  label="FTTP"    sublabel="Full fibre"          accent="#5C2D8A" delay={150} />
                <ConnectivityRing percent={fixed.ufbb_pct ?? 95}          label="UFBB"    sublabel="Ultrafast ≥300 Mbit/s" accent="#5BA0D6" delay={300} />
                <ConnectivityRing percent={mobile['4g_outdoor_pct'] ?? 99.9} label="4G out"  sublabel="Outdoor"          accent="#2D6E4E" delay={450} />
                <ConnectivityRing percent={mobile['4g_indoor_pct'] ?? 89.3}  label="4G in"   sublabel="Indoor"           accent="#6B9E56" delay={600} />
              </div>

              {/* Detail bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white/[0.04] border border-white/10 rounded-[4px] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Wifi className="w-3.5 h-3.5 text-[#FE4A2A]" strokeWidth={1.5} />
                    <span className="font-body text-[9px] uppercase tracking-wider text-white/40">Below USO</span>
                  </div>
                  <div className="font-display text-2xl font-extrabold text-[#2D6E4E]">
                    <AnimatedDecimal end={fixed.below_uso_pct ?? 0} decimals={1} suffix="%" />
                  </div>
                  <div className="font-body text-[10px] text-white/40 mt-0.5">Universal floor met</div>
                </div>
                <div className="bg-white/[0.04] border border-white/10 rounded-[4px] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Wifi className="w-3.5 h-3.5 text-[#5C2D8A]" strokeWidth={1.5} />
                    <span className="font-body text-[9px] uppercase tracking-wider text-white/40">Full fibre</span>
                  </div>
                  <div className="font-display text-2xl font-extrabold">
                    <AnimatedDecimal end={fixed.full_fibre_pct ?? 59.2} decimals={1} suffix="%" />
                  </div>
                  <div className="font-body text-[10px] text-white/40 mt-0.5">FTTP — not cable</div>
                </div>
                <div className="bg-white/[0.04] border border-white/10 rounded-[4px] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Smartphone className="w-3.5 h-3.5 text-[#6B9E56]" strokeWidth={1.5} />
                    <span className="font-body text-[9px] uppercase tracking-wider text-white/40">4G indoor</span>
                  </div>
                  <div className="font-display text-2xl font-extrabold">
                    <AnimatedDecimal end={mobile['4g_indoor_pct'] ?? 89.33} decimals={2} suffix="%" />
                  </div>
                  <div className="font-body text-[10px] text-white/40 mt-0.5">Geographic average</div>
                </div>
                <div className="bg-white/[0.04] border border-white/10 rounded-[4px] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Radio className="w-3.5 h-3.5 text-[#5BA0D6]" strokeWidth={1.5} />
                    <span className="font-body text-[9px] uppercase tracking-wider text-white/40">Superfast</span>
                  </div>
                  <div className="font-display text-2xl font-extrabold">
                    <AnimatedDecimal end={fixed.sfbb_pct ?? 99} decimals={1} suffix="%" />
                  </div>
                  <div className="font-body text-[10px] text-white/40 mt-0.5">SFBB ≥30 Mbit/s</div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer attribution + share */}
          <div className="mt-10 pt-5 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
            <p className="font-body text-[11px] text-white/35">
              Source: {stats.connectivity?.source || 'Ofcom Connected Nations, Jan 2026'} — {glanceName}.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <ShareBoroughReality />
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-1.5 font-body text-[11px] text-white/55 hover:text-[#FE4A2A] transition-colors group"
                data-testid="connectivity-callout-cta"
              >
                See where adoption breaks down
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE PROBLEM ═══ */}
      <section id="problem" className="py-24 px-6 lg:px-8" data-testid="problem-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">The problem</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[0.95] max-w-3xl mb-6">
            You can&apos;t fix what<br />you can&apos;t see.
          </h2>
          <p className="font-body text-sm sm:text-base text-[#5A5751] max-w-3xl leading-relaxed mb-6">
            The businesses that need digital support most are the ones that don&apos;t respond to surveys. They don&apos;t attend workshops. They don&apos;t apply for grants. They are trading in silence — serving customers, employing people, paying rates — and completely undetectable in the digital economy.
          </p>
          <p className="font-body text-sm sm:text-base text-[#5A5751] max-w-3xl leading-relaxed mb-6">
            The current alternatives are painfully inadequate. Consultants charge <span className="text-[#090909] font-semibold">£30,000 to £100,000</span> for reports that take months to produce and are obsolete the day they land. Ofcom maps show coverage — not consequence. Asset management platforms manage infrastructure efficiently but cannot model the human and economic impact of digital investment on the communities it reaches.
          </p>
          <p className="font-body text-sm sm:text-base text-[#5A5751] max-w-3xl leading-relaxed mb-16">
            The result? UKSPF programmes that cannot demonstrate impact. Interventions targeted at the wrong businesses. Communities losing the economic fabric that holds them together. And funders — DLUHC, DSIT, UKSPF — asking harder questions every reporting cycle.
          </p>

          {/* Bento grid — problem stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Coverage Illusion */}
            <div className="md:col-span-2 bg-white border border-[#090909]/8 p-8 rounded-[4px] relative overflow-hidden group hover:border-b-2 hover:border-b-[#FE4A2A] transition-all" data-testid="coverage-illusion-card">
              <div className="absolute top-6 right-6 w-32 h-32 bg-[#FE4A2A] rounded-full opacity-[0.04] blur-[50px] group-hover:opacity-[0.08] transition-opacity" />
              <div className="font-body text-[10px] uppercase tracking-[0.15em] text-[#FE4A2A] mb-3">The Coverage Illusion</div>
              <div className="font-display text-6xl sm:text-7xl font-extrabold tracking-tighter text-[#090909]">
                <AnimatedCounter end={Math.round(stats.ultrafast_coverage_pct || 95)} suffix="%" duration={1500} />
              </div>
              <p className="font-body text-sm text-[#5A5751] mt-2 mb-4">
                of {glanceName} premises have ultrafast broadband. The headline suggests a solved problem. The data tells a different story.
              </p>
              <ul className="space-y-1.5 mb-6 font-body text-xs text-[#5A5751]">
                {worstDistricts.map((d, i) => (
                  <li key={d.district}>• <span className="font-medium text-[#8A1A1A]">{d.invPct}%</span> of businesses in {d.district} ({d.name}) are digitally invisible.</li>
                ))}
                <li>• <span className="font-medium text-[#8A1A1A]">{(td.trading_invisible || 0).toLocaleString()}</span> trading businesses — real, operating, serving customers — cannot be found online.</li>
              </ul>
              <div className="flex gap-6">
                {worstDistricts.map((d, i) => (
                  <div key={d.district}>
                    <div className="font-display text-2xl font-extrabold text-[#8A1A1A]">{d.invPct}%</div>
                    <div className="font-body text-[10px] uppercase tracking-wider text-[#5A5751]">{d.district} invisible</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Adoption Gap */}
            <div className="bg-[#090909] text-white p-8 rounded-[4px] flex flex-col justify-between" data-testid="adoption-gap-card">
              <div>
                <div className="font-body text-[10px] uppercase tracking-[0.15em] text-[#FE4A2A] mb-3">The Adoption Gap</div>
                <p className="font-body text-sm text-white/70 leading-relaxed mb-3">
                  A business in Thornaby with full-fibre broadband and no website is not a connectivity failure. It is an <span className="text-[#FE4A2A] font-medium">adoption failure</span>. Two different problems requiring two completely different solutions.
                </p>
                <p className="font-body text-xs text-white/50 leading-relaxed">
                  Deprivation correlates with digital adoption at <span className="text-white font-medium">r = 0.95</span> — the strongest relationship in the dataset. Broadband speed correlates at <span className="text-white font-medium">r = −0.18</span> — essentially zero. The barrier is not infrastructure. It is adoption.
                </p>
              </div>
              <div className="mt-6">
                <div className="font-display text-5xl font-extrabold tracking-tight">4,749</div>
                <div className="font-body text-[10px] uppercase tracking-wider text-white/40 mt-1">trading businesses with zero digital presence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LIVE DATA ═══ */}
      <section className="py-24 px-6 lg:px-8 bg-white" data-testid="data-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">Live Register Data</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-12">
            {glanceName} at a glance
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tier Distribution */}
            <div className="border border-[#090909]/8 p-6 rounded-[4px]" data-testid="tier-pie">
              <h3 className="font-body text-[10px] uppercase tracking-[0.15em] text-[#5A5751] mb-4">Digital Tier Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={tierData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={1.5} dataKey="value">
                    {tierData.map((e, i) => <Cell key={e.name} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2 }}
                    formatter={(v) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
              {/* Invisible / Visible summary */}
              <div className="flex justify-between items-start mt-3 mb-3 px-1">
                <div>
                  <div className="font-display text-lg font-extrabold text-[#8A1A1A]">{invisibleTotal.toLocaleString()}</div>
                  <div className="font-body text-[9px] text-[#5A5751]">Invisible ({Math.round(invisibleTotal / Math.max(stats.total_businesses, 1) * 100)}%)</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-extrabold text-[#2D6E4E]">{visibleTotal.toLocaleString()}</div>
                  <div className="font-body text-[9px] text-[#5A5751]">Visible ({Math.round(visibleTotal / Math.max(stats.total_businesses, 1) * 100)}%)</div>
                </div>
              </div>
              {/* Legend */}
              <div className="grid grid-cols-1 gap-1">
                {tierData.map((t, i) => (
                  <div key={t.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: t.fill }} />
                      <span className="font-body text-[10px] text-[#5A5751]">{t.name}</span>
                    </div>
                    <span className="font-body text-[10px] font-medium text-[#090909]">{t.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* District Bar Chart */}
            <div className="lg:col-span-2 border border-[#090909]/8 p-6 rounded-[4px]" data-testid="district-bar">
              <h3 className="font-body text-[10px] uppercase tracking-[0.15em] text-[#5A5751] mb-4">Adoption by Postcode District</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={districtData} barGap={1}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="district" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                  <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2 }} />
                  <Bar dataKey="active" name="Active" fill="#2D6E4E" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="partial" name="Partial" fill="#8A5A14" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="invisible" name="Invisible" fill="#8A1A1A" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Register headline — All register vs Trading only (BDAF Final) */}
          {hi && (
            <div className="border border-[#090909]/8 rounded-[4px] mt-8 overflow-hidden" data-testid="register-headline">
              <div className="px-6 pt-5 pb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-body text-[10px] uppercase tracking-[0.15em] text-[#5A5751]">Register headline — BDAF Final Analysis</h3>
                <span className="font-body text-[9px] text-[#5A5751]/60">{hi.basis}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#090909]/8">
                <div className="p-6" data-testid="headline-all-register">
                  <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#FE4A2A] mb-3">All register</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-4xl font-extrabold tracking-tight">{hiReg.mean_bdaf}</span>
                    <span className="font-body text-xs text-[#5A5751]">mean BDAF / 5</span>
                  </div>
                  <div className="mt-3 space-y-1.5 font-body text-[11px] text-[#5A5751]">
                    <div className="flex justify-between"><span>Businesses</span><span className="font-medium text-[#090909]">{hiReg.total.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>Scoring 0–1</span><span className="font-medium text-[#8A1A1A]">{hiReg.count_0_1.toLocaleString()} ({hiReg.pct_0_1}%)</span></div>
                    <div className="flex justify-between"><span>Google-verified</span><span className="font-medium text-[#090909]">{hiReg.gmb_verified_pct}%</span></div>
                    <div className="flex justify-between"><span>Customer reviews</span><span className="font-medium text-[#090909]">{hiReg.total_reviews.toLocaleString()}</span></div>
                  </div>
                </div>
                <div className="p-6" data-testid="headline-trading-only">
                  <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#2D6E4E] mb-3">Trading only ({hiTrad.pct_of_register}% of register)</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-4xl font-extrabold tracking-tight">{hiTrad.mean_bdaf}</span>
                    <span className="font-body text-xs text-[#5A5751]">mean BDAF / 5</span>
                  </div>
                  <div className="mt-3 space-y-1.5 font-body text-[11px] text-[#5A5751]">
                    <div className="flex justify-between"><span>Trading businesses</span><span className="font-medium text-[#090909]">{hiTrad.count.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>Wholly invisible (score 0)</span><span className="font-medium text-[#8A1A1A]">{hiTrad.invisible_0_count.toLocaleString()} ({hiTrad.invisible_0_pct}%)</span></div>
                    <div className="flex justify-between"><span>Scoring 0–1</span><span className="font-medium text-[#8A1A1A]">{hiTrad.pct_0_1}%</span></div>
                    <div className="flex justify-between"><span>AI-Ready (all 5 signals)</span><span className="font-medium text-[#2D6E4E]">{hiTrad.ai_ready_count.toLocaleString()} ({hiTrad.ai_ready_pct}%)</span></div>
                  </div>
                </div>
                <div className="p-6" data-testid="headline-signals">
                  <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#5A5751] mb-3">Signal penetration — trading businesses</div>
                  <div className="space-y-2">
                    {[
                      ['S1 Website', hiSignals.website_pct],
                      ['S2 Google profile', hiSignals.google_business_pct],
                      ['S5 Reviews', hiSignals.reviews_pct],
                      ['S4 Social media', hiSignals.social_pct],
                      ['S3 Directories', hiSignals.directories_pct],
                    ].map(([label, v], i) => (
                      <div key={label}>
                        <div className="flex justify-between font-body text-[10px] text-[#5A5751] mb-0.5">
                          <span>{label}</span><span className="font-medium text-[#090909]">{v}%</span>
                        </div>
                        <div className="h-1.5 bg-[#090909]/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 65 ? '#2D6E4E' : v >= 45 ? '#8A5A14' : '#8A1A1A' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {hiSegments && (
                <div className="border-t border-[#090909]/8 px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="headline-segments">
                  {[
                    [hiSegments.website_no_gbp, 'have a website but no Google Business Profile — the highest-leverage free fix'],
                    [hiSegments.gbp_no_website, 'have a Google profile but no owned website — platform-dependent'],
                    [hiSegments.wholly_invisible, 'trading businesses with no digital presence at all — first-presence targets'],
                  ].map(([n, label], i) => (
                    <div key={label} className="flex items-baseline gap-2">
                      <span className="font-display text-xl font-extrabold text-[#FE4A2A]">{(n || 0).toLocaleString()}</span>
                      <span className="font-body text-[10px] text-[#5A5751] leading-snug">{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* District Cards — 3-Category Breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="district-cards">
            {stats.districts.map((d, i) => {
              const total = d.total || 1;
              const cat1Pct = Math.round((d.cat1_trading_invisible || 0) / total * 100);
              const cat2Pct = Math.round((d.cat2_shell_companies || 0) / total * 100);
              const cat3Pct = Math.round((d.cat3_dissolved_winding_down || 0) / total * 100);
              const activePct = Math.round((d.active_visible || 0) / total * 100);
              return (
                <div key={d.district} className="border border-[#090909]/8 p-5 rounded-[4px] bg-white hover:border-b-2 hover:border-b-[#FE4A2A] transition-all" data-testid={`district-${d.district}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-display text-lg font-extrabold tracking-tight">{d.district}</div>
                    <div className="font-body text-[10px] text-[#5A5751]">{d.total?.toLocaleString()} businesses</div>
                  </div>
                  <div className="font-body text-[10px] text-[#5A5751] mb-3">{d.name}</div>

                  {/* Stacked category bar */}
                  <div className="h-2 bg-[#F5F4F0] rounded-full overflow-hidden flex mb-3">
                    <div className="h-full bg-[#2D6E4E]" style={{ width: `${activePct}%` }} title={`Active: ${d.active_visible || 0}`} />
                    <div className="h-full bg-[#8A1A1A]" style={{ width: `${cat1Pct}%` }} title={`Trading Invisible: ${d.cat1_trading_invisible || 0}`} />
                    <div className="h-full bg-[#5A5751]" style={{ width: `${cat2Pct}%` }} title={`Shell: ${d.cat2_shell_companies || 0}`} />
                    <div className="h-full bg-[#B8B4AD]" style={{ width: `${cat3Pct}%` }} title={`Dissolved: ${d.cat3_dissolved_winding_down || 0}`} />
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-[#2D6E4E]" />
                      <span className="font-body text-[9px] text-[#5A5751]">Active: <strong className="text-[#090909]">{(d.active_visible || 0).toLocaleString()}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-[#8A1A1A]" />
                      <span className="font-body text-[9px] text-[#5A5751]">Invisible: <strong className="text-[#090909]">{(d.cat1_trading_invisible || 0).toLocaleString()}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-[#5A5751]" />
                      <span className="font-body text-[9px] text-[#5A5751]">Shell: <strong className="text-[#090909]">{(d.cat2_shell_companies || 0).toLocaleString()}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-[#B8B4AD]" />
                      <span className="font-body text-[9px] text-[#5A5751]">Dissolved: <strong className="text-[#090909]">{(d.cat3_dissolved_winding_down || 0).toLocaleString()}</strong></span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-[#090909]/5 text-right">
                    <span className="font-body text-[10px] text-[#2D6E4E] font-medium">{d.adoption_rate}% adoption rate</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ DATA METHODOLOGY ═══ */}
      <section className="py-24 px-6 lg:px-8 bg-[#FAFAF8] border-y border-[#090909]/5" data-testid="methodology-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">Verified Data Methodology</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[0.95] mb-4">
            Not all invisibility<br />is the same.
          </h2>
          <p className="font-body text-sm text-[#5A5751] max-w-2xl leading-relaxed mb-6">
            We distinguish between businesses that <em>cannot</em> be found digitally and those that were <em>never intended</em> to be found. This distinction matters — conflating the two distorts the true adoption gap and leads to misinformed intervention.
          </p>

          {/* Summary strip */}
          {stats.data_quality && (
            <div className="bg-[#090909] text-white p-5 rounded-[4px] mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="category-summary-strip">
              <div>
                <span className="font-display text-2xl sm:text-3xl font-extrabold">{stats.data_quality.three_categories_combined?.toLocaleString()}</span>
                <span className="font-body text-xs text-white/50 ml-2">of {stats.total_businesses?.toLocaleString()} businesses</span>
              </div>
              <div className="font-body text-sm text-white/60">
                fall into three distinct categories — <span className="text-[#FE4A2A] font-medium">{stats.data_quality.pct_of_register}%</span> of the total register
              </div>
            </div>
          )}

          {stats.data_quality && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {/* Cat 1: Trading but Invisible */}
              <div className="bg-white border border-[#090909]/8 p-6 rounded-[4px] border-l-4 border-l-[#8A1A1A] relative overflow-hidden group" data-testid="trading-invisible-card">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#8A1A1A] rounded-full opacity-[0.03] blur-[30px] group-hover:opacity-[0.06] transition-opacity" />
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#8A1A1A] mb-2">Category 1 &middot; 40.1%</div>
                <div className="font-display text-4xl font-extrabold tracking-tight text-[#8A1A1A]">
                  <AnimatedCounter end={stats.data_quality.trading_invisible} />
                </div>
                <div className="font-body text-xs font-medium text-[#090909] mt-1 mb-3">Trading but Digitally Invisible</div>
                <p className="font-body text-[11px] text-[#5A5751] leading-relaxed mb-4">
                  Active CH-registered businesses that are trading but invisible online. Real, operating, serving customers — but not findable via search, GMB, directories, or social media. Each is a direct economic loss and intervention target.
                </p>
                <div className="bg-[#8A1A1A]/5 rounded-[3px] p-3">
                  <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#8A1A1A] mb-1">Priority Action</div>
                  <div className="font-body text-[10px] text-[#5A5751]">GMB claim + website + digital adoption support, ranked by IMD deprivation</div>
                </div>
                {stats.data_quality.cat1_top_sectors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#090909]/5">
                    <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#5A5751]/60 mb-1.5">Top sectors</div>
                    {stats.data_quality.cat1_top_sectors.slice(0, 3).map((s, i) => (
                      <div key={s.sector} className="flex justify-between items-center">
                        <span className="font-body text-[10px] text-[#5A5751]">{s.sector}</span>
                        <span className="font-body text-[10px] font-medium text-[#090909]">{s.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cat 2: Non-Trading Shell */}
              <div className="bg-white border border-[#090909]/8 p-6 rounded-[4px] border-l-4 border-l-[#5A5751] relative overflow-hidden group" data-testid="shell-card">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#5A5751] rounded-full opacity-[0.03] blur-[30px] group-hover:opacity-[0.06] transition-opacity" />
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#5A5751] mb-2">Category 2 &middot; 5.0%</div>
                <div className="font-display text-4xl font-extrabold tracking-tight text-[#5A5751]">
                  <AnimatedCounter end={stats.data_quality.shell_non_trading} />
                </div>
                <div className="font-body text-xs font-medium text-[#090909] mt-1 mb-3">Non-Trading / Shell Companies</div>
                <p className="font-body text-[11px] text-[#5A5751] leading-relaxed mb-4">
                  Active CH status but structural indicators of non-trading activity — property vehicles, holding companies, management entities, nominees. Zero consumer-facing digital presence by design, not by neglect.
                </p>
                <div className="bg-[#5A5751]/5 rounded-[3px] p-3">
                  <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#5A5751] mb-1">Recommended</div>
                  <div className="font-body text-[10px] text-[#5A5751]">Monitor only. Not a digital adoption target.</div>
                </div>
                {stats.data_quality.cat2_top_sectors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#090909]/5">
                    <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#5A5751]/60 mb-1.5">Top sectors</div>
                    {stats.data_quality.cat2_top_sectors.slice(0, 3).map((s, i) => (
                      <div key={s.sector} className="flex justify-between items-center">
                        <span className="font-body text-[10px] text-[#5A5751]">{s.sector}</span>
                        <span className="font-body text-[10px] font-medium text-[#090909]">{s.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cat 3: Dissolved, Dormant & Winding Down */}
              <div className="bg-white border border-[#090909]/8 p-6 rounded-[4px] border-l-4 border-l-[#B8B4AD] relative overflow-hidden group" data-testid="dissolved-card">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#B8B4AD] rounded-full opacity-[0.03] blur-[30px] group-hover:opacity-[0.06] transition-opacity" />
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#B8B4AD] mb-2">Category 3 &middot; 10.0%</div>
                <div className="font-display text-4xl font-extrabold tracking-tight text-[#B8B4AD]">
                  <AnimatedCounter end={stats.data_quality.dissolved_dormant} />
                </div>
                <div className="font-body text-xs font-medium text-[#090909] mt-1 mb-3">Dissolved, Dormant & Winding Down</div>
                <p className="font-body text-[11px] text-[#5A5751] leading-relaxed mb-4">
                  Companies in formal dissolution, liquidation or administration. Proposal to Strike Off, Active-Proposal to Strike Off, Liquidation, In Administration. Ceasing to exist as legal trading entities.
                </p>
                <div className="bg-[#B8B4AD]/10 rounded-[3px] p-3">
                  <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#B8B4AD] mb-1">Recommended</div>
                  <div className="font-body text-[10px] text-[#5A5751]">Remove from active targeting. Retain for economic health and churn analysis.</div>
                </div>
                {stats.data_quality.cat3_top_sectors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#090909]/5">
                    <div className="font-body text-[9px] uppercase tracking-[0.12em] text-[#5A5751]/60 mb-1.5">Top sectors</div>
                    {stats.data_quality.cat3_top_sectors.slice(0, 3).map((s, i) => (
                      <div key={s.sector} className="flex justify-between items-center">
                        <span className="font-body text-[10px] text-[#5A5751]">{s.sector}</span>
                        <span className="font-body text-[10px] font-medium text-[#090909]">{s.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Active & Visible callout */}
          <div className="bg-[#2D6E4E]/5 border border-[#2D6E4E]/15 p-5 rounded-[4px] mb-10 flex flex-wrap items-center gap-6" data-testid="active-visible-callout">
            <div>
              <div className="font-display text-3xl font-extrabold tracking-tight text-[#2D6E4E]">
                <AnimatedCounter end={stats.data_quality?.active_visible || 5332} />
              </div>
              <div className="font-body text-xs text-[#2D6E4E]">Active & Digitally Visible</div>
            </div>
            <p className="font-body text-[11px] text-[#5A5751] leading-relaxed flex-1 min-w-[200px]">
              Companies trading with meaningful digital presence — these are the digitally included businesses of {glanceName}. <strong>{Math.round((stats.data_quality?.active_visible || 5332) / Math.max(stats.total_businesses, 1) * 100)}%</strong> of the total register.
            </p>
          </div>

          {/* Verification methodology */}
          <div className="bg-white border border-[#090909]/8 p-6 rounded-[4px]" data-testid="verification-methodology">
            <h3 className="font-body text-xs font-medium text-[#090909] mb-3">How we verify digital footprints</h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              {[
                { param: 'Website', desc: 'Active company website discoverable via domain resolution' },
                { param: 'Google Business', desc: 'Verified Google Business Profile with location and hours' },
                { param: 'Directory Listing', desc: 'Presence on Yell, Thomson, or sector-specific directories' },
                { param: 'Social Media', desc: 'Active profiles on LinkedIn, Facebook, Instagram, or X' },
                { param: 'Customer Reviews', desc: 'Reviews on Google, Trustpilot, or sector platforms' },
              ].map((p, i) => (
                <div key={p.param} className="text-center">
                  <div className="w-8 h-8 rounded-full bg-[#FE4A2A]/8 flex items-center justify-center mx-auto mb-2">
                    <span className="font-display text-sm font-extrabold text-[#FE4A2A]">{i + 1}</span>
                  </div>
                  <div className="font-body text-[11px] font-medium text-[#090909]">{p.param}</div>
                  <div className="font-body text-[9px] text-[#5A5751] mt-1 leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#090909]/5">
              <p className="font-body text-[10px] text-[#5A5751] leading-relaxed">
                Each business is scored 0–5 across these parameters using a probabilistic estimation engine calibrated against sector adoption rates, postcode-level connectivity data (Ofcom), company age, SIC code digital propensity, and company category. Scores are deterministic and reproducible per business. A score of 4–5 = <strong>Digitally Active</strong>, 2–3 = <strong>Partially Visible</strong>, 0–1 = <strong>Digitally Invisible</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WHAT IS IT ═══ */}
      <section className="py-24 px-6 lg:px-8" data-testid="what-section">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">What Is It</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[0.95] mb-6">
              Every business.<br />Every postcode.<br />Every gap.
            </h2>
            <p className="font-body text-sm text-[#5A5751] leading-relaxed mb-4">
              Adoptic makes every registered and trading business in {scopeName} visible — mapped by postcode, assessed for digital footprint, and connected to the connectivity quality of the infrastructure serving them.
            </p>
            <p className="font-body text-sm text-[#5A5751] leading-relaxed">
              Built on the Infralytics Meaningful Connectivity Audit Platform, it operationalises the finding that behind every postcode is a person — and every business — whose economic participation depends on what connectivity is actually doing for them.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Eye, label: 'Unlike Ofcom dashboards', desc: 'that show coverage but not consequence' },
              { icon: Building2, label: 'Unlike asset management', desc: 'that cannot model human impact' },
              { icon: Shield, label: 'Unlike consultancy reports', desc: 'that cost £50k, take months, and cannot be repeated' },
            ].map((item, i) => (
              <div key={item.label} className="flex gap-4 p-5 bg-white border border-[#090909]/8 rounded-[4px] hover:border-b-2 hover:border-b-[#FE4A2A] transition-all">
                <div className="w-10 h-10 rounded-[4px] bg-[#FE4A2A]/5 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-[#FE4A2A]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-body text-sm font-medium text-[#090909]">{item.label}</div>
                  <div className="font-body text-xs text-[#5A5751]">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CORRELATION FINDINGS ═══ */}
      <section className="py-24 px-6 lg:px-8 bg-white border-y border-[#090909]/5" data-testid="correlation-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">Research Findings</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[0.95] mb-4">
            The gap is not connectivity.<br />It is adoption.
          </h2>
          <p className="font-body text-sm text-[#5A5751] max-w-2xl leading-relaxed mb-12">
            We ran the correlations internally across the register — BDAF Final scores × MHCLG IoD 2025 deprivation deciles × Ofcom Connected Nations broadband data. The result is uncomfortable for conventional policy: neither deprivation nor broadband speed explains digital silence. It is structural — driven by sector and company age — and present in every neighbourhood.
          </p>

          {/* Key correlation cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="finding-paradox">
              <div className="font-display text-5xl font-extrabold tracking-tight text-[#FE4A2A]">r = {corrImd?.r_business ?? 0.04}</div>
              <div className="font-body text-xs font-medium text-[#090909] mt-2 mb-3">Deprivation × Digital Silence</div>
              <p className="font-body text-[11px] text-[#5A5751] leading-relaxed">
                Across {hiReg ? hiReg.total.toLocaleString() : 'the'} businesses, IMD 2025 decile correlates at just r&nbsp;=&nbsp;{corrImd?.r_business ?? 0.04} with scoring 0–1{corrImd?.r_district != null ? ` (district-level r = ${corrImd.r_district}, n = ${corrImd.n_districts})` : ''}. Digital silence is not confined to deprived postcodes — it is present across every deprivation band.
              </p>
            </div>

            <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="finding-illusion">
              <div className="font-display text-5xl font-extrabold tracking-tight text-[#090909]">r = {corrSpeed?.r_business ?? 0.01}</div>
              <div className="font-body text-xs font-medium text-[#090909] mt-2 mb-3">Broadband Speed × Digital Silence</div>
              <p className="font-body text-[11px] text-[#5A5751] leading-relaxed">
                Available broadband speed correlates at r&nbsp;=&nbsp;{corrSpeed?.r_business ?? 0.01} with digital silence — effectively zero. {fixed.gigabit_pct ?? 95}% gigabit coverage cannot fix a problem it did not cause. The infrastructure is there. It is not being used.
              </p>
            </div>

            <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="finding-opportunity">
              <div className="font-display text-5xl font-extrabold tracking-tight text-[#2D6E4E]">{(td.trading_invisible || 4749).toLocaleString()}</div>
              <div className="font-body text-xs font-medium text-[#090909] mt-2 mb-3">The Intervention Opportunity</div>
              <p className="font-body text-[11px] text-[#5A5751] leading-relaxed">
                {(td.trading_invisible || 4749).toLocaleString()} trading invisible businesses are real, operating, serving customers — but cannot be found online. Concentrated in the most deprived postcodes. Each one represents a direct economic loss and the highest-impact intervention target per pound spent.
              </p>
            </div>
          </div>

          {/* IMD Strata breakdown */}
          <div className="bg-[#090909] text-white p-6 rounded-[4px]" data-testid="imd-strata">
            <h3 className="font-body text-[10px] uppercase tracking-[0.15em] text-[#FE4A2A] mb-4">IMD 2025 Deprivation vs Digital Silence — {glanceName}</h3>
            <div className="grid grid-cols-5 gap-2">
              {(hi?.imd_strata || []).map((s, i) => {
                const colors = ['#8A1A1A', '#B85C2A', '#8A5A14', '#5A8A3A', '#2D6E4E'];
                return (
                <div key={s.label} className="text-center p-3 rounded-[3px]" style={{ backgroundColor: `${colors[i]}20` }}>
                  <div className="font-display text-lg font-extrabold" style={{ color: colors[i] }}>{s.silent_pct}%</div>
                  <div className="font-body text-[9px] text-white/40 mb-2">scoring 0–1</div>
                  <div className="font-body text-[10px] font-medium text-white">{s.label}</div>
                  <div className="font-body text-[8px] text-white/30">{s.sub}</div>
                  <div className="font-body text-[9px] text-white/50 mt-1">{s.businesses.toLocaleString()} businesses · BDAF {s.mean_bdaf}</div>
                </div>
                );
              })}
            </div>
            <p className="font-body text-[10px] text-white/30 mt-4 text-center">
              Digital silence rates are strikingly flat across deprivation bands — the exclusion is structural, not socioeconomic. Source: Infralytics BDAF Final × MHCLG IoD 2025.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ STRATEGIC GOALS ═══ */}
      <section className="py-24 px-6 lg:px-8 bg-[#090909] text-white" data-testid="goals-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">Product Vision</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[0.95] mb-12">
            Strategic Goals
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Real-time business landscape', desc: 'Give every Tees Valley council a real-time, evidence-grade view of its entire business digital landscape.' },
              { icon: Target, title: 'Separate infrastructure from adoption', desc: 'Separate infrastructure gaps from adoption gaps at postcode resolution — enabling targeted, cost-effective intervention.' },
              { icon: TrendingUp, title: 'National template', desc: 'Become the national template for meaningful connectivity business auditing — replicable across all 296 English local authorities.' },
              { icon: Shield, title: 'Peer-archivable finding', desc: 'Generate a third peer-archivable Infralytics empirical finding from Tees Valley data.' },
              { icon: Building2, title: 'AI economy readiness', desc: 'Support Tees Valley AI economy readiness assessment linked to the Latos £100m data centre investment.' },
            ].map((goal, i) => (
              <div key={goal.title} className="p-6 border border-white/10 rounded-[4px] hover:border-[#FE4A2A]/40 transition-colors" data-testid={`goal-${i}`}>
                <goal.icon className="w-5 h-5 text-[#FE4A2A] mb-3" strokeWidth={1.5} />
                <h3 className="font-body text-sm font-medium text-white mb-2">{goal.title}</h3>
                <p className="font-body text-xs text-white/50 leading-relaxed">{goal.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-24 px-6 lg:px-8 text-center" data-testid="cta-section">
        <div className="max-w-[820px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FE4A2A] mb-3">Already in your geography</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[0.95] mb-6">
            The businesses missing from<br />your digital economy are<br /><span className="text-[#FE4A2A]">already in your geography.</span>
          </h2>
          <p className="font-body text-sm sm:text-base text-[#5A5751] leading-relaxed mb-8 max-w-2xl mx-auto">
            Adoptic shows you exactly where they are — in minutes, not months, without a consultant in sight. The question is not whether you can afford Adoptic. It is whether your community can afford another year without it.
          </p>
          <button data-testid="cta-explore" onClick={() => navigate('/login')} className="inline-flex items-center gap-2 bg-[#FE4A2A] text-white px-8 py-4 rounded-[2px] font-body text-sm font-medium hover:bg-[#F14728] transition-colors shadow-[0_0_20px_rgba(254,74,42,0.35)]">
            Explore the Register <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ═══ BOROUGH AMPLIFICATION MAP — pre-footer social proof ═══ */}
      <BoroughAmplificationMap onShareCtaClick={() => {
        const el = document.querySelector('[data-testid="connectivity-callout"]');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }} />

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#090909]/8" data-testid="footer">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-5 w-auto brightness-0" testId="footer-logo" />
            <span className="font-body text-[11px] text-[#5A5751]/70 hidden sm:inline">Deployed for Tees Valley Combined Authority</span>
          </div>
          <div className="font-body text-[10px] text-[#5A5751]">
            Data: Companies House (OGL v3.0) · ONS IDBR · Ofcom CN Jan 2026 · MHCLG IoD 2025 · <a href="/verified" className="underline">Verified businesses</a> · <a href="/privacy" className="underline">Privacy Policy</a> · &copy; {new Date().getFullYear()} Nexintell Limited
          </div>
        </div>
      </footer>
    </div>
  );
}
